import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  type Signal,
  untracked,
} from '@angular/core';

import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { provide } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { type NoteAvatarAuthor, NoteFormComponent, NotesListComponent } from '@core/notes-legacy';
import {
  NEW_NOTE_ID,
  type NoteAssignment,
  type NoteAttachment,
  type NoteDiscardEvent,
  type NoteEditEvent,
  type NoteId,
  type NoteSave,
} from '@core/notes-types';
import {
  type MentionElement,
  type MentionElementGroup,
  MentionType,
} from '@core/prosemirror-legacy';
import { MentionsStream$, provideMentionsFooterMessage } from '@core/prosemirror-legacy/angular';
import { NotesReactionsService } from '@core/reactions-legacy';
import type { Reactions } from '@core/reactions-types';
import { TellentCurrentUserService } from '@core/tellent-logic';
import {
  DialogBodyComponent,
  DialogComponent,
  DialogHeaderComponent,
  DividerComponent,
  HeadingComponent,
} from '@core/user-interface-legacy';
import { deepEqual, getInitials } from '@core/utils-legacy';
import { type Visibility, VisibilityLevel } from '@core/visibility-types';
import { AskHRService } from '@tellent-ask-hr/core-logic';
import type { TellentMemberBrief } from '@tellent-ask-hr/core-types';
import { DynamicFormPresentComponent } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import { TellentNotesService } from '@tellent-ask-hr/notes-logic';
import type { TellentNote } from '@tellent-ask-hr/notes-types';
import { FormKindProviders } from '@tellent-ask-hr/templates-smart-ui';
import { TicketsDataService, TicketService } from '@tellent-ask-hr/tickets-logic';
import type { AskHrTicket, MoveAskTicket, UpdateAskHrTicket } from '@tellent-ask-hr/tickets-types';

import { TellentNotesReactionsService } from '../tellent-notes-reactions.service';
import { TicketInfoCardComponent } from './ticket-info-card/ticket-info-card.component';

interface TellentUiNote extends TellentNote {
  attachments: NoteAttachment[];
  body: string;
  createdAt: string;
  updatedAt: string;
  noteAssignments: NoteAssignment[];
  text: string;
  reactions: Reactions;
  visibility: Visibility;
  replies: TellentUiNote[];
  repliesIds: number[];
  replyToId: null;
  pinnedAt: null;
  triggered: null;
}

export class TicketMentionsStream$ extends BehaviorSubject<MentionElementGroup[]> {}

@Component({
  selector: 'ask-hr-ticket-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ticket-dialog.component.html',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    TranslatePipe,
    NoteFormComponent,
    HeadingComponent,
    DividerComponent,
    DynamicFormPresentComponent,
    TicketInfoCardComponent,
    NotesListComponent,
  ],
  styleUrls: ['./ticket-dialog.component.less'],
  providers: [
    ...FormKindProviders,
    TicketMentionsStream$,
    provide(MentionsStream$, { useExisting: TicketMentionsStream$ }),
    provide(NotesReactionsService, { useClass: TellentNotesReactionsService }),
    provideMentionsFooterMessage({
      useFactory: () => injectTranslate()('tlc.ask_hr.request_dialog.mentions_footer'),
    }),
  ],
})
export class TicketDialogComponent extends DialogComponent<
  { ticket: Signal<AskHrTicket | null> },
  void
> {
  private readonly ticketsDataService = inject(TicketsDataService);
  private readonly mentionsStream$ = inject(TicketMentionsStream$);
  private readonly ticketService = inject(TicketService);
  private readonly notesService = inject(TellentNotesService);
  private readonly askHrService = inject(AskHRService);
  private readonly currentUser = inject(TellentCurrentUserService);

  protected readonly NEW_NOTE_ID = NEW_NOTE_ID;

  protected presentFormItems = computed(() =>
    (this.dialogData.ticket()?.form.items ?? []).filter(item => item.type !== 'infobox'),
  );
  protected noteActive = signal(false);
  protected notes = signal<TellentUiNote[]>([]);
  protected editedNotes = signal<NoteId[]>([]);
  protected replyNotes = signal<NoteId[]>([]);
  protected notesBeingSaved = signal<NoteId[]>([]);

  protected isCurrentUserRequester = computed(() => {
    const ticket = this.dialogData.ticket();
    if (!ticket) return false;
    return ticket.requester.guid === this.currentUser.me?.guid;
  });
  protected isCurrentUserAssigned = computed(() => {
    const ticket = this.dialogData.ticket();
    if (!ticket) return false;
    return ticket.assignees.some(assignee => assignee.guid === this.currentUser.me?.guid);
  });
  protected canCancel = computed(() => {
    const ticket = this.dialogData.ticket();
    if (!ticket) return false;
    const isOpen = 'open' === ticket.status;
    return isOpen && this.isCurrentUserRequester();
  });
  protected isCancelVisible = computed(() => {
    const ticket = this.dialogData.ticket();
    if (!ticket) return false;

    return this.isCurrentUserRequester() && 'closed' !== ticket.status;
  });
  protected canChangeStatus = computed(
    () => this.isCurrentUserAssigned() || this.askHrService.hasAskManageAbility(),
  );
  protected canChangePriority = this.canChangeStatus;
  protected canChangeAssignees = this.askHrService.hasAskManageAbility;

  private currentAssigneesGuids = computed((): string[] => {
    const assigneesGuids = (this.dialogData.ticket()?.assignees ?? []).map(
      assignee => assignee.guid,
    );
    return untracked(() => this.sortAssignees(assigneesGuids));
  });

  public ngOnInit(): void {
    this.notesService.loadNotes('ask_ticket', this.dialogData.ticket()?.id ?? 0).subscribe();
    this.notesService.getNotes('ask_ticket', this.dialogData.ticket()?.id ?? 0).subscribe(notes => {
      const webappNotes = notes.map(note => ({
        id: note.id ?? NEW_NOTE_ID,
        author: note.author,
        authorGuid: note.authorGuid,
        resourceType: note.resourceType,
        bodyJson: note.bodyJson,
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
        replies: [],
        reactions: [],
        repliesIds: [],
        attachments: [],
        noteAssignments: [],
        body: '',
        text: '',
        private: false,
        pinnedAt: null,
        triggered: null,
        replyToId: null,
        visibility: {
          adminIds: [],
          level: VisibilityLevel.Public,
          roleIds: [],
        },
      }));
      this.notes.set(webappNotes);
    });
    this.notesService.fetchMentionables('ask_ticket', this.dialogData.ticket()?.id ?? 0);
    this.notesService
      .getMentionables('ask_ticket', this.dialogData.ticket()?.id ?? 0)
      .subscribe(mentionables => {
        const mentions = mentionables.map(member => this.tellentMemberToMentionable(member));
        this.mentionsStream$.next([{ header: '', data: mentions, disabled: false }]);
      });
  }

  public updateTicket(data: UpdateAskHrTicket) {
    firstValueFrom(this.ticketsDataService.updateTicket(this.dialogData.ticket()!.id, data));
  }

  public moveTicket(data: MoveAskTicket) {
    firstValueFrom(this.ticketsDataService.moveTicket(this.dialogData.ticket()!.id, data));
  }

  public updateTicketAssignees(assignees: string[]) {
    const newAssignees = this.sortAssignees(assignees);
    if (deepEqual(this.currentAssigneesGuids(), newAssignees)) {
      return;
    }

    this.updateTicket({
      assignments: newAssignees.map(assignee => ({ memberGuid: assignee })),
    });
  }

  public async cancelTicket() {
    const success = await firstValueFrom(
      this.ticketService.confirmCancel(this.dialogData.ticket()!.id),
    );
    if (success) {
      this.dialogRef.close();
    }
  }

  protected async noteSaveClick(event: NoteSave<TellentUiNote>) {
    const body = { bodyJson: event.note.bodyJson };

    this.notesBeingSaved.update(noteIds => [...noteIds, event.note.id]);
    if (Number.isInteger(event.note.id)) {
      const result = await firstValueFrom(this.notesService.updateNote(event.note.id, body));
      if (result.isSuccess) {
        this.cancelClick({ id: event.note.id });
      }
    } else {
      const result = await firstValueFrom(
        this.notesService.createNote('ask_ticket', this.dialogData.ticket()?.id ?? 0, body),
      );
      if (result.isSuccess) {
        this.noteActive.set(false);
      }
    }
    this.notesBeingSaved.update(noteIds => noteIds.filter(id => id !== event.note.id));
  }
  protected cancelClick(event: NoteDiscardEvent) {
    this.editedNotes.update(noteIds => noteIds.filter(id => id !== event.id));
    this.replyNotes.update(noteIds => noteIds.filter(id => id !== event.id));
  }

  protected noteEditClick(event: NoteEditEvent) {
    this.editedNotes.update(noteIds => [...noteIds, event.id]);
  }

  protected noteReplyClick(event: NoteEditEvent) {
    this.replyNotes.update(noteIds => [...noteIds, event.id]);
  }

  protected noteDeleteClick(event: number) {
    this.notesService.deleteNote(event);
  }

  protected localStorageKeyBuilder = (options: { parentNote: TellentUiNote }) => '';
  protected noteToMention = (note: TellentUiNote): MentionElement =>
    this.tellentMemberToMentionable(note.author);
  protected isAutomatedActionNoteFn = () => false;
  protected isOwnerFn = (note: TellentUiNote): boolean =>
    note.authorGuid === this.currentUser.me?.guid;
  protected getOwnerFn = (note: TellentUiNote): NoteAvatarAuthor => ({
    name: `${note.author.firstName} ${note.author.lastName}`,
    photoThumbUrl: note.author.avatarThumbUrl ?? '',
    hasAvatar: !!note.author.avatarThumbUrl,
    initials: getInitials(note.author.firstName, note.author.lastName),
    isGuest: false,
  });

  private sortAssignees(assignees: string[]): string[] {
    return [...assignees].sort((a, b) => (a > b ? 1 : -1));
  }

  private tellentMemberToMentionable(member: TellentMemberBrief): MentionElement {
    return {
      type: MentionType.User,
      id: <any>member.guid,
      name: `${member.firstName} ${member.lastName}`,
      avatar: member.avatarThumbUrl ?? '',
      initials: getInitials(member.firstName, member.lastName),
      hasAvatar: !!member.avatarThumbUrl,
      entity: member,
    };
  }
}
